/**
 * Published examination results, as linked from the institute's own autonomous
 * page: https://www.bietdvg.edu/autonomous-1yr
 *
 * A caution that shapes this whole module: there is NO stable "check your
 * result" URL. Each publication gets its own link carrying an encrypted token
 * (`U2FsdGVkX1…` is CryptoJS's salted-base64 prefix), and the institute has
 * moved host at least once — the 2025 releases sit on resultsbiet.com, the
 * 2026 ones on results.bietdvg.edu. So results cannot be a single link on the
 * page; they have to be a dated list that someone appends to each time the
 * Controller publishes, exactly as the source page does it.
 *
 * Transcribed from that page. Nothing here is constructed or guessed — a
 * fabricated token would simply 404, or worse, land a student on someone
 * else's publication.
 */

export type ResultKind = "result" | "reval_result" | "makeup_result";

export interface ExamResult {
  /** Programme as the source labels it. */
  programme: "B.E." | "M.Tech" | "MCA" | "MBA";
  /** Which examination, e.g. "2nd & 4th semester". */
  exam: string;
  /** Session as printed, e.g. "June/July 2026". */
  session: string;
  /** Scheme, where the source states one. */
  scheme?: string;
  kind: ResultKind;
  url: string;
  /** Called out in its own banner near the top of the page, not just as a row in the table below — for a release students are actively waiting on. */
  featured?: boolean;
}

/** What the row is called in the UI — matching the institute's own "Exam Type" column. */
export const RESULT_KIND_LABEL: Record<ResultKind, string> = {
  result: "Regular",
  reval_result: "Revaluation",
  makeup_result: "Makeup exam",
};

/** Newest first, matching the order the institute publishes them. */
export const EXAM_RESULTS: ExamResult[] = [
  {
    programme: "B.E.",
    exam: "Summer examination",
    session: "2026",
    kind: "result",
    url: "https://results.bietdvg.edu/U2FsdGVkX19nvPhuO8EbzTyy7jvJTLWtG1cB69ogf1WVmGgW94pbW8Ij3692v1EL/BE/result",
    featured: true,
  },
  {
    programme: "B.E.",
    exam: "2nd & 4th semester",
    session: "June/July 2026",
    kind: "reval_result",
    url: "https://results.bietdvg.edu/U2FsdGVkX185TpsuTqPLUAiumy9aOhRQO7a3IOOL5CzCI6SWbM3bQFsgEJ%2FQKOrb/BE/reval_result",
  },
  {
    programme: "M.Tech",
    exam: "4th semester",
    session: "June/July 2026",
    kind: "result",
    url: "https://results.bietdvg.edu/U2FsdGVkX185TpsuTqPLUAiumy9aOhRQO7a3IOOL5CzCI6SWbM3bQFsgEJ%2FQKOrb/MTech/result",
  },
  {
    programme: "B.E.",
    exam: "2nd & 4th semester",
    session: "June/July 2026",
    kind: "result",
    url: "https://results.bietdvg.edu/U2FsdGVkX185TpsuTqPLUAiumy9aOhRQO7a3IOOL5CzCI6SWbM3bQFsgEJ%2FQKOrb/BE/result",
  },
  {
    programme: "MCA",
    exam: "4th semester",
    session: "June/July 2026",
    kind: "result",
    url: "https://results.bietdvg.edu/U2FsdGVkX185TpsuTqPLUAiumy9aOhRQO7a3IOOL5CzCI6SWbM3bQFsgEJ%2FQKOrb/MCA/result",
  },
];

/*
 * The student and faculty login portals the institute publishes alongside
 * these result links now live in Header.tsx's UTILITY_LINKS — they are
 * audience entry points for the whole site, not examination content.
 */

